import { useRef, useCallback } from 'react';

/**
 * Custom hook that creates a stable callback reference
 * This helps avoid dependency issues in useEffect and other hooks
 * by providing a stable reference that always calls the latest version of the callback
 *
 * @param callback - The callback function to wrap
 * @returns A stable callback reference
 */
export const useCallbackRef = <T extends (...args: any[]) => any>(callback: T): T => {
  const callbackRef = useRef(callback);

  // Update the ref whenever the callback changes
  callbackRef.current = callback;

  // Return a stable callback that always calls the latest version
  return useCallback(
    ((...args: Parameters<T>) => {
      return callbackRef.current(...args);
    }) as T,
    [],
  );
};
